import MenuIcon from '@ingka/ssr-icon/paths/menu';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useCore } from 'contexts/core';
import { useSelector } from 'react-redux';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { KompisHyperlink, KompisSheetBody } from '@inter-ikea-kompis/react-components';
import { CoreApi } from '@inter-ikea-storageone/range';
import {
  ReportSourceEnum,
  reportAccountLogout,
  reportCreateNew,
  reportToggleHamburgerMenu,
  reportToggleModal
} from 'analytics/reporters';
import HomeDelivery, {
  ReportCurrentPageEnum
} from 'components/ProductSidebar/HomeDelivery/HomeDelivery';
import StoreSelector from 'components/ProductSidebar/StoreSelector/StoreSelector';
import { useAvailability } from 'contexts/availability';
import { getProductEntities } from 'ducks/core';
import { useAppDispatch } from 'ducks/hooks';
import { addBreadcrumb, setTag } from '@sentry/react';
import { useSceneHandler } from 'contexts/sceneHandler';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useVpc } from 'hooks/useVpc';
import { useServiceSettings } from 'contexts/serviceSettings';
import { sentryTags } from 'hooks/useSentryTags';
import { getUserName, setUserName } from 'ducks/userInfo';
import { getVpcCode } from 'ducks/vpc';
import useIsRtl from 'hooks/useIsRtl';
import { useRef, useState } from 'react';
import { AppRoutes } from 'routes';
import { SpaceProps } from 'styled-system';
import { default as Button, ButtonTypeEnum, default as SkapaButton } from 'ui/Button/Button';
import {
  isHamburgerMenuOpen,
  showHamburgerMenu,
  hideHamburgerMenu,
  getIsSavePossible
} from 'ducks/layout';
import {
  setLoadVpcSheetVisible,
  setLoadVpcSheetVisibleOnExit,
  showBookAppointmentSheet,
  showOpenDesignSheet,
  showPrintOptionsSheet,
  showPrintQuestionSheet,
  showSaveDesignToProfileSheet,
  showSaveVpcSheet,
  showShareDesignCard,
  showUnsavedChangesSheet
} from 'ducks/sheets';
import Sheet from 'ui/Sheet/Sheet';
import SheetBox from 'ui/Sheet/SheetBox';
import SheetHeader from 'ui/Sheet/SheetHeader';
import Title from 'ui/Typography/Title';
import { checkIfPrintShallBeEnabled } from 'utils/layout';
import { isKiosk, isMobile } from 'utils/platform';
import { getQuery } from 'utils/queryUtils';
import {
  HomeDeliveryWrapper,
  IconWrapper,
  KompisWrapper,
  ListWrapper,
  LoginWrapper,
  MenuButton,
  StyledLeftArrowIcon,
  StyledRightArrowIcon,
  Wrapper
} from './styles';
import { useSaveDesign } from 'hooks/useSaveDesign';
import { useServices } from 'contexts/services';
import { isSpaceEnabled } from 'utils/integration';

type MenuItemModel = {
  id: string;
  action: () => void;
  label: string;
  hide?: boolean;
  dataTestId?: string;
};

const getMenuItems = (
  actions: any,
  coreApi: CoreApi,
  printEnabled: boolean,
  bookAppointmentEnabled: boolean,
  enableSaveToProfile: boolean
): MenuItemModel[] => {
  const enabledWhenBuyableItemsInScene =
    coreApi.entities.getAllProductEntities().length === 0 ||
    coreApi.entities.getAllShoppingBagEntities().length === 0;

  return [
    {
      id: 'save',
      action: () => {
        enableSaveToProfile ? actions.save() : actions.share();
      },
      label: 'storageone.saveForLater',
      dataTestId: 'hamburger-menu-save-design',
      hide: (enableSaveToProfile && isKiosk()) || enabledWhenBuyableItemsInScene
    },
    {
      id: 'share-design',
      action: () => {
        actions.share();
      },
      label: 'storageone.menuShareDesign',
      hide: !enableSaveToProfile || enabledWhenBuyableItemsInScene
    },
    {
      id: 'myDesigns',
      action: () => {
        actions.myDesigns();
      },
      label: 'storageone.openMyDesignsTitle',
      dataTestId: 'hamburger-menu-my-designs',
      hide: !enableSaveToProfile || isKiosk()
    },
    {
      id: enableSaveToProfile ? 'open' : 'back-to-gallery',
      action: () => {
        enableSaveToProfile ? actions.open() : actions.historyPush(AppRoutes.HOME);
      },
      label: enableSaveToProfile ? 'storageone.open' : 'storageone.backToGallery'
    },
    {
      id: enableSaveToProfile ? 'create-new' : 'open',
      action: () => {
        enableSaveToProfile
          ? actions.leavePlanner(actions.historyPush(AppRoutes.HOME))
          : actions.showLoadVpcSheet();
      },
      label: enableSaveToProfile ? 'storageone.createNew' : 'storageone.open'
    },
    {
      id: 'help',
      action: () => {
        actions.help();
        actions.saveVpc();
      },
      label: 'storageone.bookAppointmentButtonText',
      hide: !bookAppointmentEnabled || isKiosk()
    },
    {
      id: 'print',
      action: () => actions.print(),
      label: 'storageone.print',
      hide: isMobile() || !printEnabled
    },
    {
      id: 'create-new',
      action: () => {
        const leaveAction = () => {
          actions.createNew();
          actions.historyReplace(AppRoutes.PLANNER_NEW);
        };
        actions.leavePlanner(leaveAction);
      },
      label: 'storageone.createNew',
      dataTestId: 'hamburger-menu-create-new',
      hide: enableSaveToProfile || enabledWhenBuyableItemsInScene
    },
    {
      id: 'change-language',
      action: () => {},
      label: 'storageone.changeLanguage',
      hide: true
    }
  ];
};

const Menu = ({ ...props }: SpaceProps) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const history = useHistory();
  const location = useLocation();
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const { accountService } = useServices();
  const dispatch = useAppDispatch();
  const vpcCode = useSelector(getVpcCode);
  const userInfo = useSelector(getUserName);
  const productEntitiesInScene = useSelector(getProductEntities);
  const isSavePossible = useSelector(getIsSavePossible);
  const enableSpacePlanning = isSpaceEnabled();
  const { storeId, stores } = useAvailability();
  const isRtl = useIsRtl();
  const [expandHamburgerMenu, setExpandHamburgerMenu] = useState(false);
  const vpc = useVpc();
  const {
    dexfSettings,
    dexfSettings: {
      general: { bookAppointmentEnabled, enableSaveToProfile }
    }
  } = useServiceSettings();

  const allowPrintOverride = getQuery().get('allowPrint');
  const printEnabled = checkIfPrintShallBeEnabled(allowPrintOverride, history.location.pathname);
  const { clearLocalScene } = useSceneHandler();

  const userLoggedIn = enableSaveToProfile && userInfo.userName !== undefined;
  const { saveToProfile, initiateLogin } = useSaveDesign();

  const menuItems = getMenuItems(
    {
      historyPush: (route: string) => history.push(route),
      historyReplace: (route: string) => history.replace(route),
      save: () => {
        if (enableSaveToProfile) {
          if (userLoggedIn) {
            saveToProfile();
          } else {
            dispatch(showSaveDesignToProfileSheet());
            reportToggleModal({
              modal: 'SaveToProfileSheet',
              visible: true,
              page: ReportCurrentPageEnum.hamburgerMenu
            });
          }
        } else {
          dispatch(showSaveVpcSheet(true));
          reportToggleModal({
            modal: 'SaveVpcSheet',
            visible: true,
            page: ReportCurrentPageEnum.hamburgerMenu
          });
        }
      },
      share: () => {
        dispatch(showShareDesignCard());
        reportToggleModal({
          modal: 'ShareDesignSheet',
          visible: true,
          page: ReportCurrentPageEnum.hamburgerMenu
        });
      },
      open: () => {
        dispatch(showOpenDesignSheet(false));
        reportToggleModal({
          modal: 'OpenDesignSheet',
          visible: true,
          page: ReportCurrentPageEnum.hamburgerMenu
        });
      },
      myDesigns: () => {
        dispatch(showOpenDesignSheet(true));
        reportToggleModal({
          modal: 'OpenDesignSheet',
          visible: true,
          page: ReportCurrentPageEnum.hamburgerMenu
        });
      },
      help: () => {
        dispatch(showBookAppointmentSheet());
        reportToggleModal({
          modal: 'BookAppointmenSheet',
          visible: true,
          page: ReportCurrentPageEnum.hamburgerMenu
        });
      },
      print: () => {
        !isKiosk() ? dispatch(showPrintQuestionSheet(true)) : dispatch(showPrintOptionsSheet(true));
        reportToggleModal({
          modal: 'PrintSheet',
          visible: true,
          page: ReportCurrentPageEnum.hamburgerMenu
        });
      },
      createNew: () => {
        clearLocalScene();
        history.replace(AppRoutes.PLANNER_NEW);
        reportCreateNew({ currentPage: window.location.href });
      },

      showLoadVpcSheet: () => {
        if (!vpcCode && productEntitiesInScene.length > 0) {
          dispatch(setLoadVpcSheetVisibleOnExit(true));
          dispatch(showUnsavedChangesSheet());
        } else {
          dispatch(setLoadVpcSheetVisible(true));
        }
      },
      leavePlanner: (leaveAction: () => void) => {
        if (productEntitiesInScene.length > 0 && location.pathname !== AppRoutes.SUMMARY) {
          dispatch(showUnsavedChangesSheet(leaveAction));
        } else {
          history.push(AppRoutes.HOME);
        }
      },
      saveVpc: async () => {
        vpc.save({ userTriggered: true });
      }
    },
    coreApi,
    printEnabled,
    bookAppointmentEnabled,
    enableSaveToProfile
  );

  const isHamburgerMenuVisible = useSelector(isHamburgerMenuOpen);

  const toggleHamburgerMenu = () => {
    dispatch(showHamburgerMenu());
    setExpandHamburgerMenu(true);
    reportToggleHamburgerMenu({ open: true });
  };

  const performAction = (id: string, action: () => void) => {
    addBreadcrumb({ message: `Hamburger menu option ${id} clicked` });
    dispatch(hideHamburgerMenu());
    setExpandHamburgerMenu(false);
    action();
  };

  // After login, the planner will be redirected to the URL set in DEXF Settings under OAuth > redirect_uri.
  const _initiateLogin = async () => {
    initiateLogin(ReportSourceEnum.hamburgerMenu);
  };

  // After logout, the planner will be redirected to the URL set in DEXF Settings under OAuth > logout_uri (which should match redirect_uri).
  const initiateLogout = () => {
    reportAccountLogout({
      source: ReportSourceEnum.hamburgerMenu
    });

    dispatch(setUserName(undefined));
    accountService().logOut();
  };

  // Set userLoggedIn tag for Sentry
  setTag(sentryTags.userLoggedIn, userLoggedIn);

  return (
    <Wrapper {...props} ref={menuRef}>
      <MenuButton>
        {!enableSpacePlanning && (
          <Button
            dataTestId="open-menu-btn"
            text={formatMessage({ id: 'storageone.openMenu' })}
            onClick={() => toggleHamburgerMenu()}
            ssrIcon={MenuIcon}
            type={ButtonTypeEnum.tertiary}
            aria-expanded={expandHamburgerMenu}
            disabled={!isSavePossible}
            expanding={false}
            iconOnly
            size={isMobile() ? 'small' : 'medium'}
          />
        )}
      </MenuButton>
      <Sheet
        onOverlayClick={() => {
          dispatch(hideHamburgerMenu());
          reportToggleHamburgerMenu({ open: false });
        }}
        visible={isHamburgerMenuVisible}
      >
        <SheetHeader
          data-testid="sheet-header"
          onCloseButtonClick={() => dispatch(hideHamburgerMenu())}
        />
        <KompisSheetBody theme={SkapaTheme}>
          <SheetBox>
            {!isKiosk() && enableSaveToProfile && (
              <LoginWrapper>
                <Title fontWeight="bold" type={ThemeFontStyleTypeEnum.headingLarge}>
                  Hej {userLoggedIn && userInfo.userName}
                </Title>
                <SkapaButton
                  data-testid={'login-button'}
                  type={userLoggedIn ? 'secondary' : 'primary'}
                  onClick={userLoggedIn ? initiateLogout : _initiateLogin}
                  text={
                    userInfo &&
                    formatMessage({
                      id: userLoggedIn ? 'storageone.logout' : 'kompis.loginButton'
                    })
                  }
                  size={'small'}
                  mb="xxs"
                  style={{ minWidth: '7rem' }}
                />
              </LoginWrapper>
            )}
            <KompisWrapper>
              {dexfSettings.kompis.serviceSettings.zipAvailabilityDataSource !== 'disabled' && (
                <HomeDeliveryWrapper id="hamburger-menu-home-delivery-btn">
                  <HomeDelivery
                    currentPage={ReportCurrentPageEnum.hamburgerMenu}
                    buttonId="hamburger-menu-home-delivery-btn"
                  />
                </HomeDeliveryWrapper>
              )}
              {stores.length > 0 && (
                <StoreSelector
                  storeId={storeId}
                  stores={stores}
                  currentPage={ReportCurrentPageEnum.hamburgerMenu}
                />
              )}
            </KompisWrapper>
            {menuItems
              .filter(p => !p.hide)
              .map(({ id, label, action, dataTestId }) => (
                <ListWrapper
                  id={id}
                  key={id}
                  onClick={() => performAction(id, action)}
                  data-testid={dataTestId}
                >
                  <Title type={ThemeFontStyleTypeEnum.headingSmall}>
                    <KompisHyperlink
                      text={formatMessage({
                        id: label
                      })}
                      theme={SkapaTheme}
                      onLinkClick={() => performAction(id, action)}
                    />
                  </Title>
                  <IconWrapper>
                    {isRtl ? <StyledLeftArrowIcon /> : <StyledRightArrowIcon />}
                  </IconWrapper>
                </ListWrapper>
              ))}
          </SheetBox>
        </KompisSheetBody>
      </Sheet>
    </Wrapper>
  );
};

export default Menu;
